import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import SideNav from "../components/SideNav";
import Header from "../components/Header";
import Loader from "../components/Loader";
import Paginate from "../components/Paginate";
import { MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import {
  fetchBlacklist,
  addToBlacklist,
  removeFromBlacklist,
} from "../actions/blacklistActions";

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
};

const modalBox = {
  borderRadius: "10px",
  width: "100%",
  maxWidth: "540px",
  maxHeight: "85vh",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
};

const modalHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "18px 24px",
};

const modalBody = {
  overflowY: "auto",
  padding: "24px",
};

const closeBtn = {
  background: "transparent",
  border: "none",
  fontSize: "22px",
  cursor: "pointer",
  color: "#aaa",
  lineHeight: 1,
};

const inputDark = {
  width: "100%",
  background: "#0b0e14",
  color: "#fff",
  border: "1px solid #2b3445",
  borderRadius: 6,
  padding: "9px 12px",
  fontSize: 13,
  boxSizing: "border-box",
};

const EMPTY_FORM = {
  account_number: "",
  bank_code: "",
  bank_name: "",
  reason: "",
  reason_category: "",
  expires_at_utc: "",
  permanent: true,
};

const entryStatus = (entry) => {
  if (entry.is_active) return "active";
  if (entry.date_deleted) return "removed";
  return "expired";
};

const fmtDate = (iso) =>
  iso && !iso.startsWith("0001")
    ? new Date(iso).toLocaleDateString("en-NG", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const AccountBlacklistScreen = () => {
  const dispatch = useDispatch();
  const initRef = useRef(true);

  const { userInfo } = useSelector((s) => s.admin2faLogin);
  const isAdmin = userInfo?.user?.role === "Admin";

  const { loading, data, error } = useSelector((s) => s.getBlacklist);
  const { loading: adding, success: addSuccess, error: addError } =
    useSelector((s) => s.addBlacklist);
  const { loading: removing, success: removeSuccess, error: removeError } =
    useSelector((s) => s.removeBlacklist);

  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [includeRemoved, setIncludeRemoved] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");

  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [confirmLift, setConfirmLift] = useState(false);

  const items = data?.data?.items || [];
  const pagination = data?.data?.page || {};

  const load = (pg = page, search = searchText, removed = includeRemoved) => {
    dispatch(fetchBlacklist(pg, 50, search, removed));
  };

  useEffect(() => {
    if (initRef.current) {
      initRef.current = false;
      load(1, "", false);
    }
  }, []);

  useEffect(() => {
    if (addSuccess) {
      toast.success("Account added to blacklist");
      setShowAdd(false);
      setForm(EMPTY_FORM);
      dispatch({ type: "ADD_BLACKLIST_RESET" });
      load();
    }
    if (addError) {
      toast.error(addError);
      dispatch({ type: "ADD_BLACKLIST_RESET" });
    }
  }, [addSuccess, addError]);

  useEffect(() => {
    if (removeSuccess) {
      toast.success("Block lifted successfully");
      setShowDetail(false);
      setConfirmLift(false);
      setSelected(null);
      dispatch({ type: "REMOVE_BLACKLIST_RESET" });
      load();
    }
    if (removeError) {
      toast.error(removeError);
      dispatch({ type: "REMOVE_BLACKLIST_RESET" });
    }
  }, [removeSuccess, removeError]);

  const handleSearch = () => {
    setPage(1);
    setSearchText(searchInput);
    load(1, searchInput, includeRemoved);
  };

  const handleClear = () => {
    setSearchInput("");
    setSearchText("");
    setPage(1);
    load(1, "", includeRemoved);
  };

  const handlePageClick = (pg) => {
    setPage(pg);
    load(pg, searchText, includeRemoved);
  };

  const validateForm = () => {
    if (!/^\d{10,34}$/.test(form.account_number))
      return "Account number must be 10–34 digits";
    if (!form.bank_code.trim()) return "Please select a bank";
    if (form.reason.trim().length < 3 || form.reason.trim().length > 500)
      return "Reason must be 3–500 characters";
    if (!form.permanent && !form.expires_at_utc)
      return "Please set an expiry date or mark as permanent";
    if (!form.permanent && new Date(form.expires_at_utc) <= new Date())
      return "Expiry date must be in the future";
    return "";
  };

  const handleAdd = () => {
    const err = validateForm();
    if (err) { setFormError(err); return; }
    setFormError("");
    const payload = {
      account_number: form.account_number,
      bank_code: form.bank_code,
      reason: form.reason,
    };
    if (form.bank_name.trim()) payload.bank_name = form.bank_name;
    if (form.reason_category) payload.reason_category = form.reason_category;
    payload.expires_at_utc = form.permanent ? null : form.expires_at_utc;
    dispatch(addToBlacklist(payload));
  };

  const handleLift = () => {
    dispatch(removeFromBlacklist({ id: selected.id }));
  };

  const openDetail = (entry) => {
    setSelected(entry);
    setConfirmLift(false);
    setShowDetail(true);
  };

  const statusBadge = (entry) => {
    const s = entryStatus(entry);
    if (s === "active") return <span className="badge bg-danger">Blocked</span>;
    if (s === "removed") return <span className="badge bg-success">Lifted</span>;
    return <span className="badge bg-secondary">Expired</span>;
  };

  return (
    <>
      <SideNav />
      <section className="page-container">
        <div className="page-content">
          <Header />
          <div className="page-body">
            <h1>Account Blacklist</h1>

            {/* Filter bar */}
            <div className="filter-box" style={{ justifyContent: "flex-start", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="Search account number, bank, reason…"
                style={{
                  background: "#1a1a1a",
                  color: "#fff",
                  border: "1px solid #444",
                  borderRadius: 5,
                  padding: "5px 12px",
                  fontSize: 13,
                  minWidth: 260,
                }}
              />
              <div
                className="filter"
                onClick={handleSearch}
                style={{ cursor: "pointer", color: "white" }}
              >
                Search
              </div>
              {(searchInput || searchText) && (
                <div
                  className="filter"
                  onClick={handleClear}
                  style={{ cursor: "pointer", color: "white", background: "#555" }}
                >
                  Clear
                </div>
              )}
              <label style={{ display: "flex", alignItems: "center", gap: 7, color: "white", fontSize: 13, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={includeRemoved}
                  onChange={(e) => {
                    setIncludeRemoved(e.target.checked);
                    setPage(1);
                    load(1, searchText, e.target.checked);
                  }}
                  style={{ accentColor: "#14b8a6", width: 14, height: 14 }}
                />
                Include removed / expired
              </label>
              <div
                className="filter"
                onClick={() => { setForm(EMPTY_FORM); setFormError(""); setShowAdd(true); }}
                style={{ cursor: "pointer", color: "white", marginLeft: "auto" }}
              >
                + Add to Blacklist
              </div>
            </div>

            {/* Table */}
            {loading ? (
              <Loader />
            ) : error ? (
              <p style={{ color: "#dc3545" }}>{error}</p>
            ) : (
              <Paginate
                totPages={pagination.total_page || 1}
                currentPage={page}
                pageClicked={handlePageClick}
              >
                <MDBTable responsive className="table">
                  <MDBTableHead>
                    <tr>
                      <th>Account Number</th>
                      <th>Bank</th>
                      <th>Reason</th>
                      <th>Category</th>
                      <th>Expires</th>
                      <th>Date Added</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </MDBTableHead>
                  <MDBTableBody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={8} style={{ textAlign: "center", padding: 32, color: "#aaa" }}>
                          No entries found
                        </td>
                      </tr>
                    ) : (
                      items.map((entry) => (
                        <tr key={entry.id}>
                          <td style={{ fontWeight: 600, fontFamily: "monospace" }}>
                            {entry.account_number}
                          </td>
                          <td>
                            <div>{entry.bank_name || "—"}</div>
                            <small style={{ color: "#aaa" }}>{entry.bank_code}</small>
                          </td>
                          <td style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={entry.reason}>
                            {entry.reason}
                          </td>
                          <td>
                            {entry.reason_category
                              ? <span className="badge bg-info">{entry.reason_category}</span>
                              : "—"}
                          </td>
                          <td>{entry.expires_at_utc ? fmtDate(entry.expires_at_utc) : "Permanent"}</td>
                          <td>{fmtDate(entry.date_created)}</td>
                          <td>{statusBadge(entry)}</td>
                          <td>
                            <span
                              onClick={() => openDetail(entry)}
                              style={{ cursor: "pointer", color: "#14b8a6", fontWeight: 600, fontSize: 13 }}
                            >
                              View
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </MDBTableBody>
                </MDBTable>
              </Paginate>
            )}
          </div>
        </div>
      </section>

      {/* Add to Blacklist Modal */}
      {showAdd && (
        <div style={modalOverlay} onClick={() => setShowAdd(false)}>
          <div style={modalBox} className="referral-modal-box" onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader} className="referral-modal-header">
              <h3 style={{ margin: 0, fontSize: 18 }}>Add to Blacklist</h3>
              <button style={closeBtn} onClick={() => setShowAdd(false)}>✕</button>
            </div>
            <div style={modalBody} className="referral-modal-body">
              {formError && (
                <div style={{ background: "#2a0a0a", border: "1px solid #dc3545", borderRadius: 6, padding: "10px 14px", color: "#f5c6cb", fontSize: 13, marginBottom: 16 }}>
                  {formError}
                </div>
              )}

              <DarkField label="Account Number *">
                <input
                  type="text"
                  value={form.account_number}
                  onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))}
                  placeholder="e.g. 0123456789"
                  style={inputDark}
                />
              </DarkField>

              <DarkField label="Bank Code *">
                <input
                  type="text"
                  value={form.bank_code}
                  onChange={(e) => setForm((f) => ({ ...f, bank_code: e.target.value }))}
                  placeholder="e.g. 000014"
                  style={inputDark}
                />
              </DarkField>

              <DarkField label="Bank Name">
                <input
                  type="text"
                  value={form.bank_name}
                  onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))}
                  placeholder="e.g. Access Bank"
                  style={inputDark}
                />
              </DarkField>

              <DarkField label="Reason *">
                <textarea
                  value={form.reason}
                  onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder="Internal reason (3–500 chars). Not shown to customers."
                  rows={3}
                  style={{ ...inputDark, resize: "vertical" }}
                />
                <small style={{ color: "#aaa" }}>{form.reason.length} / 500</small>
              </DarkField>

              <DarkField label="Reason Category">
                <select
                  value={form.reason_category}
                  onChange={(e) => setForm((f) => ({ ...f, reason_category: e.target.value }))}
                  style={inputDark}
                >
                  <option value="">— Select category —</option>
                  <option value="fraud">Fraud</option>
                  <option value="chargeback">Chargeback</option>
                  <option value="compliance">Compliance</option>
                  <option value="review">Review</option>
                </select>
              </DarkField>

              <DarkField label="Block Duration">
                <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 13, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={form.permanent}
                    onChange={(e) => setForm((f) => ({ ...f, permanent: e.target.checked }))}
                  />
                  Permanent (no expiry)
                </label>
                {!form.permanent && (
                  <input
                    type="datetime-local"
                    value={form.expires_at_utc}
                    onChange={(e) => setForm((f) => ({ ...f, expires_at_utc: e.target.value }))}
                    style={{ ...inputDark, marginTop: 8 }}
                  />
                )}
              </DarkField>

              <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                <button
                  onClick={() => setShowAdd(false)}
                  style={{ flex: 1, background: "#333", color: "#fff", border: "none", borderRadius: 6, padding: "10px", cursor: "pointer", fontSize: 14 }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  disabled={adding}
                  style={{ flex: 1, background: adding ? "#555" : "#dc3545", color: "#fff", border: "none", borderRadius: 6, padding: "10px", fontWeight: 700, cursor: adding ? "not-allowed" : "pointer", fontSize: 14 }}
                >
                  {adding ? "Adding…" : "Add to Blacklist"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && selected && (
        <div style={modalOverlay} onClick={() => { setShowDetail(false); setSelected(null); setConfirmLift(false); }}>
          <div style={modalBox} className="referral-modal-box" onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader} className="referral-modal-header">
              <h3 style={{ margin: 0, fontSize: 18 }}>Blacklist Entry #{selected.id}</h3>
              <button style={closeBtn} onClick={() => { setShowDetail(false); setSelected(null); setConfirmLift(false); }}>✕</button>
            </div>
            <div style={modalBody} className="referral-modal-body">
              <div style={{ marginBottom: 16 }}>{statusBadge(selected)}</div>
              <DarkInfoRow label="Account Number" value={selected.account_number} mono />
              <DarkInfoRow label="Bank" value={`${selected.bank_name || "—"} (${selected.bank_code})`} />
              <DarkInfoRow label="Reason" value={selected.reason} />
              <DarkInfoRow label="Category" value={selected.reason_category || "—"} />
              <DarkInfoRow label="Expires" value={selected.expires_at_utc ? fmtDate(selected.expires_at_utc) : "Permanent"} />
              <DarkInfoRow label="Date Added" value={fmtDate(selected.date_created)} />
              {selected.date_deleted && (
                <DarkInfoRow label="Date Lifted" value={fmtDate(selected.date_deleted)} />
              )}

              {isAdmin && entryStatus(selected) === "active" && (
                <div style={{ marginTop: 24, background: "#1a0a0a", border: "1px solid #444", borderRadius: 8, padding: 16 }}>
                  {!confirmLift ? (
                    <>
                      <p style={{ color: "#f5c6cb", fontSize: 13, margin: "0 0 12px" }}>
                        Lifting this block will allow transactions to/from this account again. This action is logged.
                      </p>
                      <button
                        onClick={() => setConfirmLift(true)}
                        style={{ background: "#28a745", color: "#fff", border: "none", borderRadius: 6, padding: "9px 18px", fontWeight: 700, cursor: "pointer", fontSize: 13 }}
                      >
                        Lift Block
                      </button>
                    </>
                  ) : (
                    <>
                      <p style={{ color: "#f5c6cb", fontSize: 13, fontWeight: 600, margin: "0 0 12px" }}>
                        Confirm: lift the block on {selected.account_number}?
                      </p>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={() => setConfirmLift(false)}
                          style={{ flex: 1, background: "#333", color: "#fff", border: "none", borderRadius: 6, padding: "9px", cursor: "pointer", fontSize: 13 }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleLift}
                          disabled={removing}
                          style={{ flex: 1, background: removing ? "#555" : "#28a745", color: "#fff", border: "none", borderRadius: 6, padding: "9px", fontWeight: 700, cursor: removing ? "not-allowed" : "pointer", fontSize: 13 }}
                        >
                          {removing ? "Lifting…" : "Yes, Lift Block"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

function DarkField({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#aaa", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.3 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function DarkInfoRow({ label, value, mono }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: "1px solid #2a2a2a", gap: 12, fontSize: 13 }}>
      <span style={{ color: "#aaa", flexShrink: 0 }}>{label}</span>
      <span style={{ color: "#fff", fontWeight: 500, textAlign: "right", fontFamily: mono ? "monospace" : undefined }}>
        {value || "—"}
      </span>
    </div>
  );
}

export default AccountBlacklistScreen;
